import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useHybridAuth } from './useHybridAuth';
import { ServiceType, type Rate } from '../lib/calculations';

export type { Rate };

interface RequestParams {
  companyId?: string;
}

const getRatesRequest = async (
  token: string,
  options?: RequestParams
): Promise<Rate[]> => {
  try {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
    };

    if (options?.companyId) {
      headers['x-company-id'] = options.companyId;
    }

    const response = await api.get<Rate[]>('/rates', { headers });

    // Handle both response formats: direct array or wrapped in rates object
    if (Array.isArray(response.data)) {
      return response.data;
    } else if (response.data && Array.isArray((response.data as any).rates)) {
      return (response.data as any).rates;
    } else {
      console.warn('Unexpected response format:', response.data);
      return [];
    }
  } catch (error) {
    console.error('Error fetching rates:', error);
    return [];
  }
};

export const useGetRates = (
  params?: RequestParams,
  config?: { enabled?: boolean }
) => {
  const { getAccessTokenSilently, isAuthenticated } = useHybridAuth();

  return useQuery({
    ...config,
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getRatesRequest(token, params);
    },
    queryKey: ['rates', params],
    enabled: config?.enabled !== false && isAuthenticated && !!params?.companyId,
  });
};

